/**
 * Banner de localização (#230/#229, ADR-0014): acende para gestores quando o
 * restaurante está sem localização CONFIRMADA — sugestão pendente do
 * backfill (confirma em 1 toque) ou endereço ausente (link à tela). NUNCA
 * bloqueia a operação do salão — é só um aviso no painel.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { locationApi, locationBannerState } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { getTenantSlug } from '../../lib/auth.js'

export function LocationBanner({ onChange }: { onChange?: () => void }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  const { slug: routeSlug } = useParams()
  const tenantSlug = routeSlug ?? getTenantSlug()

  // banner some da própria tela quando a sessão é kitchen/waiter (403)
  const { data, isError } = useQuery({
    queryKey: ['location-banner'],
    queryFn: () => locationApi.get(),
    retry: false,
    staleTime: 60_000,
  })

  const confirm = useMutation({
    mutationFn: () => locationApi.confirmSuggestion(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['location-banner'] })
      void qc.invalidateQueries({ queryKey: ['admin-location'] })
      onChange?.()
    },
  })

  const state = locationBannerState(isError ? null : data)
  if (state.kind === 'hidden') return null

  return (
    <div
      data-testid="location-banner"
      className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex flex-wrap items-center gap-3"
    >
      <span className="text-sm text-amber-900">
        <strong>{t('admin.location.banner_title')}</strong>
        {state.kind === 'confirm_suggestion' ? (
          <span> {t('admin.location.banner_body_suggestion')}</span>
        ) : (
          <span> {t('admin.location.banner_body_missing')}</span>
        )}
      </span>
      {state.kind === 'confirm_suggestion' && (
        <button
          type="button"
          onClick={() => confirm.mutate()}
          disabled={confirm.isPending}
          data-testid="location-banner-confirm"
          className="text-sm font-semibold text-amber-900 underline hover:text-amber-700 disabled:opacity-50"
        >
          {t('admin.location.banner_cta')}
        </button>
      )}
      <Link
        to={`/r/${tenantSlug}/location`}
        className="text-sm text-indigo-600 hover:underline"
        data-testid="location-banner-link"
      >
        {t('admin.location.banner_link')}
      </Link>
    </div>
  )
}
